// Phase 8 — Workspace context: which workspace is the user looking at,
// and what role do they have inside it. Persisted in localStorage so it
// survives reloads. The active workspace is independent of route — most
// listings call /api/analyses or /api/validation/findings unscoped (across
// all workspaces the user is a member of) and apply the active workspace
// as a client-side filter UI affordance.
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { listWorkspaces } from "./api";

const STORAGE_KEY = "scope_active_workspace_v1";

const WorkspaceCtx = createContext(null);

export function WorkspaceProvider({ children }) {
    const { user } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [activeId, setActiveIdRaw] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) || null; } catch (_) { return null; }
    });
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const refresh = useCallback(async () => {
        if (!user) {
            setWorkspaces([]);
            return;
        }
        setLoading(true); setErr(null);
        try {
            const r = await listWorkspaces();
            setWorkspaces(r.workspaces || []);
            // If activeId isn't in the returned list, fall back to first non-system workspace
            if (r.workspaces && r.workspaces.length > 0) {
                const stillThere = activeId && r.workspaces.some((w) => w.id === activeId);
                if (!stillThere) {
                    const personal = r.workspaces.find((w) => !w.is_system && w.role === "owner") || r.workspaces[0];
                    setActiveIdRaw(personal.id);
                    try { localStorage.setItem(STORAGE_KEY, personal.id); } catch (_) { /* */ }
                }
            }
        } catch (e) {
            setErr(e.message || "Failed to load workspaces");
        } finally {
            setLoading(false);
        }
    }, [user, activeId]);

    // `refresh` is a stable useCallback from this same context; re-running on
    // its identity change would cause an infinite refresh loop because refresh
    // mutates `activeId` which is one of its own deps. We only want to refetch
    // when the user changes (sign-in / sign-out).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refresh(); }, [user?.user_id]);

    const setActiveId = useCallback((id) => {
        setActiveIdRaw(id);
        try { localStorage.setItem(STORAGE_KEY, id || ""); } catch (_) { /* */ }
    }, []);

    const value = useMemo(() => {
        const active = workspaces.find((w) => w.id === activeId) || null;
        return {
            workspaces, activeId, active, role: active?.role || null,
            setActiveId, refresh, loading, err,
        };
    }, [workspaces, activeId, setActiveId, refresh, loading, err]);

    return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export function useWorkspace() {
    const ctx = useContext(WorkspaceCtx);
    return ctx || {
        workspaces: [], activeId: null, active: null, role: null,
        setActiveId: () => {}, refresh: () => {}, loading: false, err: null,
    };
}
